/**
 * 任务 WebSocket Hook
 *
 * 用于订阅索引任务进度更新
 */

import { useEffect, useRef, useCallback, useState } from 'react';

export interface TaskProgressEvent {
    type: 'progress';
    task_id: number;
    status: string;
    progress: number;
    current_stage?: string;
    processed_chunks?: number;
    total_chunks?: number;
    estimated_remaining?: string;
}

export interface TaskStageChangeEvent {
    type: 'stage_changed';
    task_id: number;
    previous_stage: string;
    current_stage: string;
    progress: number;
}

export interface TaskCompletedEvent {
    type: 'completed';
    task_id: number;
    status: 'completed';
    total_chunks: number;
    duration_seconds?: number;
}

export interface TaskFailedEvent {
    type: 'failed';
    task_id: number;
    status: 'failed';
    error_message: string;
    retry_count: number;
    can_retry: boolean;
}

export interface TaskSubscribedEvent {
    type: 'subscribed';
    task_ids?: number[];
}

export interface TaskUnsubscribedEvent {
    type: 'unsubscribed';
    task_ids?: number[];
}

export type TaskWebSocketEvent =
    | TaskProgressEvent
    | TaskStageChangeEvent
    | TaskCompletedEvent
    | TaskFailedEvent
    | TaskSubscribedEvent
    | TaskUnsubscribedEvent;

export interface UseTaskWebSocketOptions {
    /** 订阅的任务 ID 列表 */
    taskIds: number[];
    /** 进度更新回调 */
    onProgress?: (event: TaskProgressEvent) => void;
    /** 阶段变更回调 */
    onStageChanged?: (event: TaskStageChangeEvent) => void;
    /** 任务完成回调 */
    onCompleted?: (event: TaskCompletedEvent) => void;
    /** 任务失败回调 */
    onFailed?: (event: TaskFailedEvent) => void;
    /** 是否自动连接 */
    autoConnect?: boolean;
    /** 重连间隔（毫秒） */
    reconnectInterval?: number;
}

export interface UseTaskWebSocketReturn {
    /** WebSocket 连接状态 */
    connected: boolean;
    /** 已订阅的任务 ID 列表 */
    subscribedTaskIds: number[];
    /** 手动连接 */
    connect: () => void;
    /** 断开连接 */
    disconnect: () => void;
    /** 订阅任务 */
    subscribe: (taskIds: number[]) => void;
    /** 取消订阅 */
    unsubscribe: (taskIds: number[]) => void;
}

/**
 * 获取 WebSocket URL
 */
function getWebSocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const baseUrl = window.location.host || 'localhost:3000';
    return `${protocol}//${baseUrl}/api/v1/knowledge/tasks/ws`;
}

const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * 任务 WebSocket Hook
 *
 * 关键设计：所有回调和可变数据都通过 useRef 保存，
 * 从而使 connect 函数的依赖保持稳定，避免无限重渲染/重连循环。
 */
export function useTaskWebSocket(options: UseTaskWebSocketOptions): UseTaskWebSocketReturn {
    const {
        taskIds,
        onProgress,
        onStageChanged,
        onCompleted,
        onFailed,
        autoConnect = true,
        reconnectInterval = 3000,
    } = options;

    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const [connected, setConnected] = useState(false);
    const [subscribedTaskIds, setSubscribedTaskIds] = useState<number[]>([]);

    // 用 ref 保存所有回调，避免作为 connect 的依赖
    const callbacksRef = useRef({
        onProgress,
        onStageChanged,
        onCompleted,
        onFailed,
    });
    useEffect(() => {
        callbacksRef.current = { onProgress, onStageChanged, onCompleted, onFailed };
    }, [onProgress, onStageChanged, onCompleted, onFailed]);

    // 用 ref 保存需要订阅的 ID 集合
    const activeTaskIdsRef = useRef<Set<number>>(new Set(taskIds || []));

    /** 发送订阅消息 */
    const sendSubscribe = useCallback((ids: number[]) => {
        if (wsRef.current?.readyState === WebSocket.OPEN && ids.length > 0) {
            wsRef.current.send(JSON.stringify({
                type: 'subscribe',
                task_ids: ids,
            }));
            console.log('[TaskWebSocket] 发送订阅:', ids);
        }
    }, []);

    // 当外部 taskIds 属性变化时，合并到 ref 中
    const taskIdsStr = JSON.stringify(taskIds || []);
    useEffect(() => {
        const parsed: number[] = JSON.parse(taskIdsStr);
        let changed = false;
        for (const id of parsed) {
            if (!activeTaskIdsRef.current.has(id)) {
                activeTaskIdsRef.current.add(id);
                changed = true;
            }
        }
        // 如果有新 ID 且已连接，立即发送订阅
        if (changed && wsRef.current?.readyState === WebSocket.OPEN) {
            sendSubscribe(parsed);
        }
    }, [sendSubscribe, taskIdsStr]);

    /** 清理重连定时器 */
    const clearReconnectTimer = useCallback(() => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    }, []);

    /** 连接 WebSocket - 依赖为空，永远不会因外部状态变化而重建 */
    const connect = useCallback(() => {
        // 如果已经连接或正在连接，跳过
        if (wsRef.current?.readyState === WebSocket.OPEN ||
            wsRef.current?.readyState === WebSocket.CONNECTING) {
            return;
        }

        try {
            const url = getWebSocketUrl();
            console.log('[TaskWebSocket] 连接到:', url);

            const ws = new WebSocket(url);

            ws.onopen = () => {
                console.log('[TaskWebSocket] 连接成功');
                setConnected(true);
                clearReconnectTimer();
                reconnectAttemptsRef.current = 0;

                // 连接成功后，发送所有累积的订阅
                const allIds = Array.from(activeTaskIdsRef.current);
                if (allIds.length > 0) {
                    sendSubscribe(allIds);
                }
            };

            ws.onmessage = (event) => {
                try {
                    const data: TaskWebSocketEvent = JSON.parse(event.data);

                    switch (data.type) {
                        case 'progress':
                            callbacksRef.current.onProgress?.(data);
                            break;
                        case 'stage_changed':
                            callbacksRef.current.onStageChanged?.(data);
                            break;
                        case 'completed':
                            callbacksRef.current.onCompleted?.(data);
                            break;
                        case 'failed':
                            callbacksRef.current.onFailed?.(data);
                            break;
                        case 'subscribed':
                            setSubscribedTaskIds(data.task_ids || []);
                            break;
                        case 'unsubscribed':
                            setSubscribedTaskIds(data.task_ids || []);
                            break;
                    }
                } catch (error) {
                    console.error('[TaskWebSocket] 解析消息失败:', error);
                }
            };

            ws.onerror = (error) => {
                console.warn('[TaskWebSocket] 连接错误:', error);
            };

            ws.onclose = () => {
                console.log('[TaskWebSocket] 连接关闭');
                setConnected(false);
                wsRef.current = null;

                // 限制重连次数，防止无限循环
                if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
                    reconnectAttemptsRef.current++;
                    const delay = reconnectInterval * reconnectAttemptsRef.current;
                    console.log(`[TaskWebSocket] ${delay}ms 后尝试第 ${reconnectAttemptsRef.current} 次重连...`);
                    reconnectTimerRef.current = setTimeout(() => {
                        connect();
                    }, delay);
                } else {
                    console.warn(`[TaskWebSocket] 已达最大重连次数 ${MAX_RECONNECT_ATTEMPTS}，停止重连`);
                }
            };

            wsRef.current = ws;
        } catch (error) {
            console.error('[TaskWebSocket] 创建连接失败:', error);
        }
    }, [clearReconnectTimer, reconnectInterval, sendSubscribe]);

    /** 断开连接 */
    const disconnect = useCallback(() => {
        clearReconnectTimer();
        reconnectAttemptsRef.current = MAX_RECONNECT_ATTEMPTS; // 阻止重连

        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }

        setConnected(false);
        setSubscribedTaskIds([]);
    }, [clearReconnectTimer]);

    /** 订阅任务 */
    const subscribe = useCallback((newTaskIds: number[]) => {
        // 记录到 ref 中以便重连时恢复
        for (const id of newTaskIds) {
            activeTaskIdsRef.current.add(id);
        }

        // 如果已连接，立即发送
        sendSubscribe(newTaskIds);
    }, [sendSubscribe]);

    /** 取消订阅 */
    const unsubscribe = useCallback((taskIdsToUnsubscribe: number[]) => {
        for (const id of taskIdsToUnsubscribe) {
            activeTaskIdsRef.current.delete(id);
        }

        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'unsubscribe',
                task_ids: taskIdsToUnsubscribe,
            }));
        }
    }, []);

    /** 组件挂载时自动连接，卸载时断开 */
    useEffect(() => {
        if (autoConnect) {
            connect();
        }

        return () => {
            disconnect();
        };
    }, [autoConnect, connect, disconnect]);

    return {
        connected,
        subscribedTaskIds,
        connect,
        disconnect,
        subscribe,
        unsubscribe,
    };
}
